import cn from 'classnames';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getErrorIconSrc } from '@shared/components/Icon/iconSets/getErrorIconSrc.ts';
import { i18nNameSpace } from '@shared/consts/i18n';
import { useUserDetails } from '@shared/providers/UserDetailsProvider/useUserDetails.tsx';
import { routePaths } from '@shared/router/routePaths';
import { determineAdmanagerBrandData } from '@shared/utils/determineAdmanagerBrandData.ts';
import { logError } from '@shared/utils/logError.ts';
import { navigateTo } from '@shared/utils/navigateTo.tsx';

import styles from './ErrorPage.module.scss';

interface Props {
  statusCode?: 404 | 500;
}

function ErrorPage({ statusCode }: Props) {
  const isNotFoundPage = statusCode === 404;
  const determinedTranslationKey = statusCode || 500;
  const navigate = useNavigate();
  const { t } = useTranslation(i18nNameSpace.COMMON);
  const { countryCode } = useUserDetails();
  const { logoSrc, altText } = determineAdmanagerBrandData(countryCode);
  const errorIconSrc = getErrorIconSrc(countryCode);

  const goBackToCampaignsList = () => {
    void navigateTo(navigate, routePaths.campaigns).catch(logError);
  };

  return (
    <section className={styles.container}>
      <div className={styles.gradientLine} />
      <header className={styles.headerContainer}>
        <button className={styles.logoRedirection} onClick={goBackToCampaignsList}>
          <img src={logoSrc} className={styles.newLogoText} alt={altText} />
        </button>
      </header>
      <main className={styles.mainContainer}>
        <img src={errorIconSrc} className={styles.errorImg} alt='Error' />

        {statusCode && <Tag className={styles.errorTag} value={`${t('errorPage.error')} ${statusCode}`} />}

        <h2 className={styles.title}>{t(`errorPage.${determinedTranslationKey}.title`)}</h2>

        <p className={styles.secondaryText}>
          {t(`errorPage.${determinedTranslationKey}.description`)}{' '}
          {!isNotFoundPage && <span className={styles.block}>{t('errorPage.tryAgain')}</span>}
        </p>

        {isNotFoundPage && (
          <Button onClick={goBackToCampaignsList} className={styles.goBackButton}>
            {t('errorPage.goBack')}
          </Button>
        )}

        <p className={cn(styles.secondaryText, styles.support)}>
          {t('errorPage.support')}{' '}
          <a href='mailto:admanagerfailure@mediaset.it' className={styles.contactEmail}>
            admanagerfailure@mediaset.it
          </a>
        </p>
        <p className={styles.secondaryText}>
          {t('errorPage.thanks')} <span className={styles.block}>{t('errorPage.teamName')}</span>
        </p>
      </main>
    </section>
  );
}

export default ErrorPage;
